/** SUNTRONIX ONE — Role-based dashboard (/dashboard). Widgets chosen per role by Admin. */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RefreshCw, Settings2 } from "lucide-react";

const TONE = { good: "text-emerald-700", warn: "text-amber-600", bad: "text-rose-700" };
const fmt = (w) => {
  if (w.value == null) return "—";
  if (w.fmt === "inr") return `₹${Number(w.value).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  if (w.fmt === "pct") return `${w.value}%`;
  return Number(w.value).toLocaleString("en-IN");
};

export default function RoleDashboard() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const view = sp.get("view") || "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/role-dashboards/me", { params: view ? { dashboard: view } : {} });
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load dashboard"); }
    finally { setLoading(false); }
  }, [view]);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="flex items-center justify-center gap-2 py-24 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading dashboard…</div>;
  if (!data) return null;

  return (
    <div className="max-w-[1400px] mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-5" data-testid="role-dashboard">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <div className="font-mono text-[10px] font-semibold tracking-[0.18em] uppercase text-sky-600">Role dashboard{data.customised ? " · customised by Admin" : ""}</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900" data-testid="rd-title">{data.label}</h1>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          {data.can_switch && (
            <select value={data.dashboard} data-testid="rd-switch"
              onChange={(e) => { const n = new URLSearchParams(sp); if (e.target.value === data.own_dashboard) n.delete("view"); else n.set("view", e.target.value); setSp(n); }}
              className="h-8 rounded-md border border-slate-200 bg-white text-[12px] px-2">
              {Object.entries(data.dashboards).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          )}
          {data.can_switch && (
            <button onClick={() => navigate("/admin/role-dashboards")} data-testid="rd-customise"
              className="h-8 px-2.5 rounded-md border border-slate-200 bg-white text-[12px] inline-flex items-center gap-1.5 hover:bg-slate-50"><Settings2 className="h-3.5 w-3.5" /> Customise</button>
          )}
          <button onClick={load} data-testid="rd-refresh" className="h-8 px-2.5 rounded-md border border-slate-200 bg-white text-[12px] inline-flex items-center gap-1.5 hover:bg-slate-50"><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="rd-widgets">
        {data.widgets.map((w) => (
          <button key={w.key} onClick={() => navigate(w.route)} data-testid={`rd-widget-${w.key}`}
            className="text-left rounded-lg border border-slate-200 bg-white px-4 py-3.5 hover:-translate-y-px hover:shadow-sm hover:border-slate-300 transition-[transform,box-shadow,border-color]">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold truncate">{w.label}</div>
            <div className={`font-heading text-2xl sm:text-3xl font-bold tracking-tighter mt-1 ${w.error ? "text-slate-300" : TONE[w.tone] || "text-slate-900"}`}>{fmt(w)}</div>
            {w.hint && <div className="text-[11px] text-slate-400 mt-0.5 truncate">{w.hint}</div>}
            {w.breakdown && (
              <div className="mt-2 space-y-1">
                {w.breakdown.map((b) => {
                  const max = Math.max(1, ...w.breakdown.map((x) => x.value ?? x.count));
                  const v = b.value ?? b.count;
                  return (
                    <div key={b.key} className="flex items-center gap-2 text-[11px]">
                      <span className="w-14 text-slate-500 shrink-0">{b.key}</span>
                      <div className="flex-1 h-1.5 bg-slate-100 rounded"><div className="h-1.5 bg-sky-500 rounded" style={{ width: `${(v / max) * 100}%` }} /></div>
                      <span className="font-mono text-slate-600 w-16 text-right">{b.value != null ? `₹${Number(b.value).toLocaleString("en-IN", { maximumFractionDigits: 0 })}` : b.count}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
